import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  HostBinding,
  ElementRef,
  ViewChild,
  ContentChild,
  TemplateRef,
  forwardRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { DropdownFilerPipe } from './pipes/dropdown-filter.pipe';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

export type DropdownTheme = 'light' | 'dark';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, DropdownFilerPipe, FormsModule],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],

})
export class DropdownComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder!: string

  /** Label shown above the dropdown */
  @Input() label = '';

  /** Is the field required (adds the red * ) */
  @Input() required = false;

  /** Close the panel automatically after clicking an item inside it */
  @Input() closeOnInsideClick = true;

  /** Enable multi-select mode */
  @Input() multiSelect = false;

  /** Show clear button to reset selection */
  @Input() clearable = false;

  /** 'light' | 'dark' */
  @Input() theme: DropdownTheme = 'dark';

  /** Disable the whole trigger */
  @Input() disabled = false;

  /** Emits whenever the open state changes */
  @Output() openChange = new EventEmitter<boolean>();

  /** Emits the selected item (the full object) whenever it changes */
  @Output() selectedItemChange = new EventEmitter<any>();

  @Input() data: any[] | null = null;

  /** Key used to display the label. Supports dot-notation for nested values, e.g. 'name.en' */
  @Input() optionKey!: string;

  /** Key used as the underlying value. Supports dot-notation for nested values, e.g. 'id' or 'name.en' */
  @Input() valueKey!: string;

  @Input() filter = false;
  @Input() filterKey!: string;
  filterText = '';

  @Input() width!: string | null;

  private _selectedItem: any = null;
  private _selectedItems: any[] = [];

  get selectedItem(): any {
    return this.multiSelect ? this._selectedItems : this._selectedItem;
  }

  get selectedItems(): any[] {
    return this._selectedItems;
  }

  @ContentChild('itemTemplate') itemTemplate?: TemplateRef<any>;

  @ContentChild('selectedTemplate') selectedTemplate?: TemplateRef<any>;

  @ViewChild('trigger') triggerRef?: ElementRef<HTMLButtonElement>;

  isOpen = false;

  @HostBinding('attr.data-theme')
  get themeAttr() {
    return this.theme;
  }

  constructor(private elRef: ElementRef<HTMLElement>) { }

  private _pendingValue: any = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.resolveSelection();
    }
  }

  // ===== ControlValueAccessor =====
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  writeValue(value: any): void {
    this._pendingValue = value;
    this.resolveSelection();
  }

  private resolveSelection(): void {
    const value = this._pendingValue;
    if (this.multiSelect) {
      const vals = Array.isArray(value) ? value : [];
      this._selectedItems = this.valueKey && this.data
        ? this.data.filter(d => vals.includes(this.resolvePath(d, this.valueKey)))
        : vals;
    } else if (this.valueKey && this.data && value != null) {
      const val = typeof value === 'object' ? this.resolvePath(value, this.valueKey) : value;
      this._selectedItem =
        this.data.find(d => this.resolvePath(d, this.valueKey) === val) ?? value;
    } else {
      this._selectedItem = value ?? null;
    }
  }
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  // ================================
  selectItem(item: any): void {
    if (this.multiSelect) {
      // Multi-select mode: toggle selection
      const itemValue = this.valueKey ? this.resolvePath(item, this.valueKey) : item;
      const index = this._selectedItems.findIndex(selected => {
        const selectedValue = this.valueKey ? this.resolvePath(selected, this.valueKey) : selected;
        return selectedValue === itemValue;
      });

      if (index > -1) {
        // Already selected, remove it
        this._selectedItems.splice(index, 1);
      } else {
        // Not selected, add it
        this._selectedItems.push(item);
      }

      const values = this._selectedItems.map(i =>
        this.valueKey ? this.resolvePath(i, this.valueKey) : i
      );
      this.onChange(values);
      this.onTouched();
      this.selectedItemChange.emit(this._selectedItems);

      // Don't close panel in multi-select mode by default
    } else {
      // Single select mode
      this._selectedItem = item;
      this.onChange(this.valueKey ? this.resolvePath(item, this.valueKey) : item);
      this.onTouched();
      this.selectedItemChange.emit(item);

      if (this.closeOnInsideClick) {
        this.close();
      }
    }
  }

  isItemSelected(item: any): boolean {
    if (this.multiSelect) {
      const itemValue = this.valueKey ? this.resolvePath(item, this.valueKey) : item;
      return this._selectedItems.some(selected => {
        const selectedValue = this.valueKey ? this.resolvePath(selected, this.valueKey) : selected;
        return selectedValue === itemValue;
      });
    } else {
      if (!this._selectedItem) return false;
      const itemValue = this.valueKey ? this.resolvePath(item, this.valueKey) : item;
      const selectedValue = this.valueKey ? this.resolvePath(this._selectedItem, this.valueKey) : this._selectedItem;
      return itemValue === selectedValue;
    }
  }

  clearSelection(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation(); // منع فتح الـ dropdown لما تدوس على X
    }

    if (this.multiSelect) {
      this._selectedItems = [];
      this.onChange([]);
    } else {
      this._selectedItem = null;
      this.onChange(null);
    }

    this.onTouched();
    this.selectedItemChange.emit(this.multiSelect ? [] : null);
  }

  get hasValue(): boolean {
    if (this.multiSelect) {
      return this._selectedItems.length > 0;
    } else {
      return this._selectedItem != null;
    }
  }

  getLabel(item: any): string {
    if (item == null) return '';
    return this.optionKey ? this.resolvePath(item, this.optionKey) : item;
  }

  /**
   * Resolves a possibly nested key on an object.
   * Supports both flat keys ('name') and dot-notation nested keys ('name.en').
   */
  private resolvePath(obj: any, path: string): any {
    return path
      .split('.')
      .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  }

  toggle(): void {
    if (this.disabled) return;
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    if (this.disabled || this.isOpen) return;
    this.isOpen = true;
    this.openChange.emit(true);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.openChange.emit(false);
    this.triggerRef?.nativeElement.focus();
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}